import {
  HotelPurchaseModel,
  HotelPurchaseMutationInput,
} from 'api-hooks/order/hotel';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import SaleRelatedTable from 'legacy-modules/order/component/sale-related-table';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import {
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { HotelPurchaseFormSchema, HotelPurchaseFormType } from './form-type';
import OrdersDetailCard from './order-detail-card';
import ScheduleSection from './schedule';
import HotelPurchaseSelectInput from './select-input';
import SummaryHotelPurchase from './summary';
import useGetDefaultValues from '../hooks/use-get-default-values';
import { FormContent, FormDivider } from '../style';

interface Props {
  hotelPurchase?: HotelPurchaseModel;
  onSubmit: (
    input: HotelPurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<HotelPurchaseModel | undefined>;
  renderError?: React.ReactNode;
  hotelSale?: HotelSaleModel;
  defaultValues?: HotelPurchaseFormType;
}

export default function HotelPurchaseForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { hotelPurchase, renderError, hotelSale } = props;

  const defaultValues = useGetDefaultValues({
    hotelPurchase,
    hotelSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(HotelPurchaseFormSchema());

  const methods = useForm<HotelPurchaseFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: HotelPurchaseFormType) => {
      const {
        hotelSale,
        tempTransactionAt,
        agentStatus,
        remainingTransactionQuota,
        isExceedingQuota,
        //remove all unused data
        ...rest
      } = values;

      try {
        const needApproval =
          rest.transactionAt?.getTime() !== tempTransactionAt?.getTime();

        const result = {
          ...rest,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('hotel-purchases');
  const { onVoid, voidMutate, isActive, voidPermission } = useVoidAction(
    'hotel-purchases',
    hotelPurchase,
  );
  const { resolverButton } = useResolveApproval(
    'hotel-purchases',
    hotelPurchase,
  );
  const { requestApprovalButton } = useApprovalAction(
    'hotel-purchases',
    hotelPurchase,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'hotel-purchases',
    hotelPurchase,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'hotel-purchases',
    hotelPurchase,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (hotelPurchase) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
          {revertVoidButton}
        </div>
      );
    }
    return null;
  }, [
    hotelPurchase,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: HotelPurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('invoiceId', value.sale?.id || '');
      methods.setValue('tourId', value.tour?.id || '');
      methods.setValue('type', value.type || 'sales');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!hotelPurchase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        hotelPurchase
          ? `${t('common:hotel_purchase')} - ${hotelPurchase.number}`
          : undefined
      }
      keyName={asPath}
    >
      <FormContent>
        {renderError}
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={hotelPurchase}
              noSubmit
              onDelete={onVoid}
              noCancel
              noDelete={editable || !voidPermission}
              title={
                hotelPurchase ? (
                  <TitleStatus
                    type="purchase"
                    title={`${t('common:hotel_purchase')} - ${
                      hotelPurchase.number
                    }`}
                    status={hotelPurchase.status}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:hotel_purchase') })
                )
              }
              isDeleteLoading={voidMutate.isLoading}
              deleteText={t('common:void')}
              noEdit={!isActive || !permission.update}
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        <div>
          {hotelPurchase &&
            !!(hotelPurchase?.releasePurchases || []).length && (
              <SalePurchaseRefundTable data={hotelPurchase.releasePurchases} />
            )}
          {hotelPurchase && (
            <SaleRelatedTable
              data={hotelPurchase.sale}
              navigation={{
                sale: 'HotelSalesView',
                purchase: 'HotelPurchasesView',
              }}
            />
          )}
          {hotelPurchase &&
            (!!hotelPurchase.parentPurchase ||
              !!(hotelPurchase.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={hotelPurchase}
                navigation="HotelPurchasesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <HotelPurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketOrders:group_code')}
                placeholder={t('ticketOrders:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <OrdersDetailCard hotelPurchase={hotelPurchase} />

          <ScheduleSection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <OtherPriceComponent
            type="hotel-purchases"
            data={props.hotelPurchase}
          />
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryHotelPurchase hotelPurchase={hotelPurchase} />
    </Form>
  );
}
